import {
  ArcoCreateOrUpdateDtoInt32EntityChange,
  getGetGrafoNodoQueryKey,
  NodoDto,
  NodoTipo,
  useEliminaNodo,
  useGetGrafoNodo,
  useGetNodo,
  useUpdateGrafoArchi,
} from "@/api";
import { useAuth } from "@/context/useAuth";
import Button from "@/elements/Button";
import { useConfirmationDialog } from "@/utils/useConfirmationDialog";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import {
  Backdrop,
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import type { Node } from "@xyflow/react";
import { ReactFlow, ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NodoDialog from "../NodoDialog";
import GrafoNodo from "./GrafoNodo";
import { useLayoutedReactFlowNodesAndEdges } from "./useLayoutedReactFlowNodesAndEdges";
import GrafoGateway from "./GrafoGateway";
import GrafoRipetitore from "./GrafoRipetitore";

export default function Grafo({ nodoId }: { nodoId: number }) {
  return (
    <ReactFlowProvider>
      <GrafoWrapped nodoId={nodoId} />
    </ReactFlowProvider>
  );
}

const nodeTypes = {
  default: GrafoNodo,
  gateway: GrafoGateway,
  ripetitore: GrafoRipetitore,
};

const proOptions = { hideAttribution: true };

function GrafoWrapped({ nodoId: aziendaId }: { nodoId: number }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: aziendaData } = useGetNodo(aziendaId);
  const {
    data: grafoData,
    isLoading: isLoadingGrafo,
    isFetching: isFetchingGrafo,
    error,
  } = useGetGrafoNodo(aziendaId);
  const { mutateAsync: updateArchi, isLoading: isUpdatingArchi } =
    useUpdateGrafoArchi({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries(
            getGetGrafoNodoQueryKey(aziendaId)
          );
        },
      },
    });

  const loading = isLoadingGrafo || isFetchingGrafo || isUpdatingArchi;

  const {
    nodes,
    edges,
    archiByDestinazioneId,
    nodoById,
    autoLayout,
    onNodesChange,
    onEdgesChange,
  } = useLayoutedReactFlowNodesAndEdges(
    aziendaData?.data ?? null,
    grafoData?.data ?? null
  );

  const [nodoDialogState, setNodoDialogState] = useState<{
    open: boolean;
    padre: NodoDto | null;
    nodo: NodoDto | null;
    tipo: NodoTipo | null;
  }>({
    open: false,
    padre: null,
    nodo: null,
    tipo: null,
  });

  // Stato per il context menu
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    nodeId: string | null;
    nodo: NodoDto | null;
  } | null>(null);

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault();
      setContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
        nodeId: node.id,
        nodo: node.data.nodo as NodoDto,
      });
    },
    []
  );

  const onPaneContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent) => {
      event.preventDefault();
      setContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
        nodeId: null,
        nodo: null,
      });
    },
    []
  );

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleAddNodeToParent = () => {
    if (contextMenu?.nodo) {
      setNodoDialogState({
        open: true,
        padre: contextMenu.nodo,
        nodo: null,
        tipo: "Contenitore",
      });
    }
    handleContextMenuClose();
  };

  const handleAddGatewayToParent = () => {
    if (contextMenu?.nodo) {
      setNodoDialogState({
        open: true,
        padre: contextMenu.nodo,
        nodo: null,
        tipo: "Gateway",
      });
    }
    handleContextMenuClose();
  };

  const handleAddRipetitoreToParent = () => {
    if (contextMenu?.nodo) {
      setNodoDialogState({
        open: true,
        padre: contextMenu.nodo,
        nodo: null,
        tipo: "Ripetitore",
      });
    }
    handleContextMenuClose();
  };

  const handleEditNode = () => {
    if (contextMenu?.nodo) {
      setNodoDialogState({
        open: true,
        padre: null,
        nodo: contextMenu.nodo,
        tipo: null,
      });
    }
    handleContextMenuClose();
  };

  const { hasPermission } = useAuth();

  const { mutateAsync: deleteNodo } = useEliminaNodo({
    mutation: {
      onSuccess: () => {
        toast.success(t("grafo.toast.posizioneEliminata"));
        queryClient.invalidateQueries(getGetGrafoNodoQueryKey(aziendaId));
      },
      onError: () => {
        toast.error(t("grafo.toast.erroreEliminazione"));
      },
    },
  });

  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const handleDeleteNode = () => {
    if (contextMenu?.nodo) {
      askConfirmation({
        title: t("grafo.confermaElimina.titolo"),
        message: t("grafo.confermaElimina.messaggio", {
          nome: contextMenu.nodo.nome,
        }),
        message2:
          contextMenu.nodo.tipo === "Azienda" ||
          contextMenu.nodo.tipo === "Contenitore"
            ? t("grafo.confermaElimina.messaggioDettaglio")
            : undefined,
        okColor: "error",
        onConfirm: () => {
          return deleteNodo({
            id: contextMenu!.nodo!.id,
          });
        },
      });
    }
    handleContextMenuClose();
  };

  // Callback for when a new edge is created (child dragged to new parent)
  const onConnect = useCallback(
    async (connection: { source: string; target: string }) => {
      const sourceId = parseInt(connection.source);
      const targetId = parseInt(connection.target);

      const parentNode = nodoById[sourceId];
      const childNode = nodoById[targetId];

      console.log("child", childNode);
      console.log("parent", parentNode);

      if (childNode && parentNode) {
        if (
          (childNode.tipo === "Contenitore" ||
            childNode.tipo === "Gateway" ||
            childNode.tipo === "Ripetitore") &&
          (parentNode.tipo === "Contenitore" || parentNode.tipo === "Azienda")
        ) {
          const currentParents = archiByDestinazioneId[childNode.id] ?? [];

          let parentExists = false;
          for (const p of currentParents) {
            if (p.sorgenteId === parentNode.id) {
              parentExists = true;
            }
          }

          if (!parentExists) {
            // devo sostituire il padre di questo nodo
            const parentsToRemove = currentParents.filter((x) => {
              const parentNodo = nodoById[x.sorgenteId!];
              return parentNodo.tipo === "Contenitore";
            });

            const changes: ArcoCreateOrUpdateDtoInt32EntityChange[] = [];
            for (const p of parentsToRemove) {
              changes.push({
                changeType: "removed",
                id: p.id,
                entity: p,
              });
            }
            changes.push({
              changeType: "added",
              entity: {
                sorgenteId: parentNode.id,
                destinazioneId: childNode.id,
                sorgenteAziendaId: aziendaId,
              },
            });

            updateArchi({ data: changes });
          }
        }
      }

      // if (!childNode || !parentNode) return;
      // setLoading(true);
      // try {
      //   await collegaNodo({
      //     childId: Number((childNode.data as any).nodo.id),
      //     parentId: Number((parentNode.data as any).nodo.id),
      //   });
      //   await queryClient.invalidateQueries(getGetGrafoNodoQueryKey(nodoId));
      // } catch (e) {
      //   toast.error(t("grafo.errori.relazionePadreFiglio"));
      // }
      // setLoading(false);
    },
    [nodes, queryClient, aziendaId, nodoById]
  );

  const handleCloseNodoDialog = useCallback(() => {
    setNodoDialogState({
      open: false,
      nodo: null,
      padre: null,
      tipo: null,
    });

    queryClient.invalidateQueries(getGetGrafoNodoQueryKey(aziendaId));
  }, [aziendaId]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Box display="flex" flexDirection="row">
        <Typography variant="h4">
          {t("grafo.titolo", {
            nome: aziendaData?.data?.nome ?? "",
          })}
        </Typography>
        <Box flex={1} />
        <Box display="flex" flexDirection="row">
          <Button
            size="small"
            onClick={autoLayout}
            variant="outlined"
            color="primary"
            title={t("grafo.autoLayout.titolo")}
            startIcon={<AccountTreeIcon />}
          >
            {t("grafo.autoLayout.label")}
          </Button>
        </Box>
      </Box>

      <NodoDialog
        open={nodoDialogState.open}
        padreId={nodoDialogState.padre?.id}
        nodoId={nodoDialogState.nodo?.id}
        tipoNodo={nodoDialogState.tipo ?? undefined}
        onClose={handleCloseNodoDialog}
      />

      {confirmationDialog}

      <Backdrop open={loading} style={{ zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeContextMenu={onNodeContextMenu}
        onPaneContextMenu={onPaneContextMenu}
        onConnect={onConnect}
        fitView
        style={{ marginTop: 8, border: "1px solid gray", borderRadius: "5px" }}
        nodeTypes={nodeTypes}
        proOptions={proOptions}
      />

      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {/* {!contextMenu?.nodo && (
          <MenuItem onClick={handleAddNode}>
            {t("grafo.menu.aggiungiNodo")}
          </MenuItem>
        )} */}

        {(contextMenu?.nodo?.tipo === "Azienda" ||
          contextMenu?.nodo?.tipo === "Contenitore") && (
          <MenuItem onClick={handleAddNodeToParent}>
            {t("grafo.menu.aggiungiPosizione")}
          </MenuItem>
        )}

        {(contextMenu?.nodo?.tipo === "Azienda" ||
          contextMenu?.nodo?.tipo === "Contenitore") && (
          <MenuItem onClick={handleAddGatewayToParent}>
            {t("grafo.menu.aggiungiGateway")}
          </MenuItem>
        )}

        {(contextMenu?.nodo?.tipo === "Azienda" ||
          contextMenu?.nodo?.tipo === "Contenitore") && (
          <MenuItem onClick={handleAddRipetitoreToParent}>
            {t("grafo.menu.aggiungiRipetitore")}
          </MenuItem>
        )}

        {contextMenu?.nodo && (
          <MenuItem onClick={handleEditNode}>{t("azioni.modifica")}</MenuItem>
        )}
        {contextMenu?.nodo &&
          contextMenu?.nodo?.tipo !== "Azienda" &&
          hasPermission("ContenitoreDelete") && (
            <MenuItem onClick={handleDeleteNode}>{t("azioni.elimina")}</MenuItem>
          )}
      </Menu>
    </div>
  );
}
